<script setup lang="ts">
    import { ref, onMounted, inject } from 'vue';
    import { useRouter } from 'vue-router';
    import { loadForms, loadWidgetConfiguration, saveWidgetConfiguration } from '@views/Web/Forms.access';
    import type { FormListItem, WidgetConfiguration } from '@views/Web/Forms.types';
    import { parse } from '@views/Web/WidgetService'; //shoul dbe { parse, widgetRuleset } but rollup doesnt understand
    import FormsListItem from '@views/Web/FormsList/FormsListItem.vue';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import List from '@shared/components/List/List.vue';
    import { copyToClipboard } from '@views/Web/copyToClipboard';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const router = useRouter();

    const newForm = function () {
        router.push({ name: 'FormsNew' });
    };

    const formsList = ref<FormListItem[]>([]);
    const widgetConfiguration = ref<WidgetConfiguration | null>(null);
    const isLoading = ref(true);
    const isSaving = ref(false);
    const currentFormsRules = ref<string>('');
    const hasChanges = ref<boolean>(false);
    let widgetRules: any | null = null; //should be widget ruleset but rollup doesnt understand
    let current = ref(-1);
    let hasWidgetExecutableScope = false;

    onMounted(async () => {
        formsList.value = await loadForms();

        await loadWidgetConfiguration().then((wc: any) => {
            let data = wc.Data ?? '{"rules": []}';
            widgetConfiguration.value = wc;
            widgetRules = parse(data);
            currentFormsRules.value = widgetRules.serialize();
            isLoading.value = false;
        });
    });

    const requireScopeParams: vRequireScopeParams = {
        Feature: 'Widgets',
        Permission: Permissions.Execute,
    };

    hasWidgetExecutableScope = scopeAccess.hasScope(requireScopeParams);

    const saveChanges = (publish: boolean = false) => {
        if (isSaving.value) return;
        isSaving.value = true;

        let json = widgetRules?.serialize();
        let wc = widgetConfiguration.value;

        if (wc != null) {
            wc.Data = json ?? '';
            saveWidgetConfiguration(json ?? '', publish).then((newConfiguration: any) => {
                isSaving.value = false;
                widgetRules && (currentFormsRules.value = widgetRules.serialize());
                widgetConfiguration.value = newConfiguration;
                bsAlert({ title: $Resources.FormsSaveAndPublishAlertHeading, content: $Resources.FormsSaveAndPublishAlertBody });
            });
        } else {
            alert('debug, shold not happen, remove at pr');
        }

        current = ref(-1);
    };

    const copyForm = (webContentId: string) => {
        router.push({ name: 'FormsCopy', params: { copyFromWebContentId: webContentId } });
    };

    const orderOptions = () => {
        return [
            { display: $Resources.LastModifiedLabel, key: 'Modified' },
            { display: $Resources.CreatedLabel, key: 'Created' },
            { display: $Resources.NameLabel, key: 'Name' },
        ];
    };
</script>

<template>
    <div class="p-lg-r p-lg-l">
        <h1>
            {{ $Resources.FormsHeading }}
            <small>{{ formsList.length }} {{ $Resources.FormsCreatedLabel }}</small>
        </h1>

        <div v-if="widgetConfiguration != null" v-require-scope="{ Feature: 'Widgets', Permission: Permissions.Read }">
            <div class="d-flex flex-wrap flex-even gap-lg">
                <BSFormInputText
                    class="form-group"
                    :modelValue="widgetConfiguration.TestWidgetPath ?? ''"
                    :label="$Resources.FormsWidgetScriptTestLabel"
                    :disabled="true">
                    <template #button>
                        <button class="btn btn-default" type="button" @click="copyToClipboard(widgetConfiguration?.TestWidgetPath as string)">
                            {{ $Resources.CopyBtn }}
                        </button>
                    </template>
                </BSFormInputText>

                <BSFormInputText
                    class="form-group"
                    :modelValue="widgetConfiguration.WidgetPath ?? ''"
                    :label="$Resources.FormsWidgetScriptMainLabel"
                    :disabled="true">
                    <template #button>
                        <button class="btn btn-default" type="button" @click="copyToClipboard(widgetConfiguration?.WidgetPath as string)">
                            {{ $Resources.CopyBtn }}
                        </button>
                    </template>
                </BSFormInputText>
            </div>

            <BsAlert
                :show="widgetConfiguration.HasUnpublishedChanges && hasWidgetExecutableScope"
                :type="AlertTypes.Warning"
                :title="$Resources.FormsAlertUnpublishedChangesHeading"
                :content="$Resources.FormsAlertUnpublishedChangesBody"
                :dismissible="false"
                :buttonText="$Resources.FormsAlertUnpublishedChangesBtn"
                @buttonClick="saveChanges(true)"></BsAlert>

            <BsAlert
                :show="widgetConfiguration.HasUnpublishedChanges && !hasWidgetExecutableScope"
                :type="AlertTypes.Warning"
                :title="$Resources.FormsAlertUnpublishedChangesNoPermissionHeading"
                :content="$Resources.FormsAlertUnpublishedChangesNoPermissionBody"
                :dismissible="false" />
        </div>
    </div>

    <List
        :items="formsList"
        :loading="isLoading"
        :filterKeys="['Name']"
        :orderOptions="orderOptions()"
        :itemId="'WebContentId'"
        :hasCheckedItems="current !== -1">
        <template #toolbar>
            <div class="m-auto-l d-flex flex-wrap gap-sm">
                <button @click="newForm" class="btn btn-primary" v-show="current === -1 && !isSaving">
                    <span class="glyphicon glyphicon-plus m-xxs-r"></span>
                    {{ $Resources.FormsCreateNewBtn }}
                </button>

                <button class="btn btn-subtle" type="button" v-show="current > -1 || isSaving" @click="current = -1">
                    {{ $Resources.FormsCancelBtn }}
                </button>

                <button
                    class="btn btn-primary"
                    type="button"
                    v-show="current > -1 || isSaving"
                    @click="saveChanges(false)"
                    :disabled="!hasChanges"
                    :class="{ 'is-busy': isSaving }">
                    {{ $Resources.FormsSaveAndPublishBtn }}
                </button>
            </div>
        </template>
        <template #item="{ item, index }">
            <FormsListItem
                :item="item"
                :index="index"
                :current="current"
                :currentFormsRules="currentFormsRules"
                :widgetRuleset="widgetRules"
                @update:hasChanges="(newValue) => (hasChanges = newValue)"
                @edit-rules="(itemIndex) => (current = itemIndex)"
                @copy-form="copyForm" />
        </template>
    </List>
</template>
